import React, { useState, useEffect, useRef } from 'react'
import styled from 'styled-components'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import { usePatientContext } from '@/v2/contexts/PatientContext'
import {
  useCreateProgression,
  useDeleteProgression,
  useRenameProgression,
} from '@/v2/hooks/useProgressions'
import useDetectDevice from '@/v2/hooks/useDetectDevice'
import NoFolders from './NoFolders'
import ConfirmDelete from './ConfirmDelete'
import MobileFolders from './mobile/Folders'
import Folders from './Folders'
import StandardImages from './StandardImages'
import Gallery from './Gallery'

const GalleryWrapper = styled.div`
  ${({ $isMobile }) =>
    $isMobile
      ? ''
      : `
    display: flex;
    align-items: flex-start;
    flex: 1;
    width: 100%;
    margin-right: 45px;
    margin-top: 45px;
  `}
`
const parse = value => parseInt(value, 10)

const Imaging = () => {
  const { isMobile } = useDetectDevice()
  const navigate = useNavigate()
  const location = useLocation()
  const { id, progression, series } = useParams()

  const { createProgression } = useCreateProgression()
  const { deleteProgression } = useDeleteProgression()
  const { renameProgression } = useRenameProgression()
  const {
    patient,
    progressions,
    onAddProgression,
    onRenameProgression,
    onDeleteProgression,
    onUploadImage,
  } = usePatientContext()
  const selectedProgression = progressions.find(
    item => parse(item.id) === parse(progression)
  )
  const selectedSeries = (selectedProgression?.series || []).find(
    item => parse(item.id) === parse(series)
  )
  const [folderId, setFolderId] = useState(
    progression ? progression : progressions[0]?.id
  )
  const [folderDate, setFolderDate] = useState(
    progression ? selectedProgression?.startDate : progressions[0]?.startDate
  )
  const [isCreationMode, setIsCreationMode] = useState(false)
  const [isEditMode, setIsEditMode] = useState(false)
  const [openDeleteConfirmation, setOpenDeleteConfirmation] = useState(false)
  const processedCropResultRef = useRef(false)
  const hasInitialNavigatedRef = useRef(false)

  useEffect(() => {
    if (!selectedProgression && !!progression) {
      navigate(`/v2/patient/${id}/imaging`)
    }
  }, [selectedProgression, progression, navigate, id])

  useEffect(() => {
    if (hasInitialNavigatedRef.current || progression) {
      return
    }

    if (progressions.length > 0) {
      const firstProgression = progressions[0]
      if (firstProgression.series?.length > 0) {
        hasInitialNavigatedRef.current = true
        navigate(
          `/v2/patient/${id}/imaging/progression/${firstProgression.id}/series/${firstProgression.series[0].id}`,
          { replace: true }
        )
      }
    }
  }, [progression, progressions, navigate, id])

  useEffect(() => {
    if (location.state?.cropImageResult && !processedCropResultRef.current) {
      processedCropResultRef.current = true
      const {
        uploadedImage,
        progressionId,
        seriesId,
      } = location.state.cropImageResult
      if (uploadedImage && progressionId && seriesId) {
        onUploadImage(progressionId, seriesId, uploadedImage)
      }
      navigate(location.pathname, { replace: true, state: {} })
    } else if (!location.state?.cropImageResult) {
      processedCropResultRef.current = false
    }
  }, [location.state, location.pathname, navigate, onUploadImage])

  const handleUpdateFolders = newFolder => {
    onAddProgression(newFolder)
    setFolderId(newFolder.id)
    setFolderDate(newFolder.startDate)
  }

  const handleCreate = async value => {
    try {
      await createProgression(patient.id, value || 'Progression', newFolder => {
        handleUpdateFolders(newFolder)
        setIsCreationMode(false)
      })
    } catch (error) {
      console.error(error)
    }
  }

  const handleDelete = async () => {
    try {
      const progressionToDelete = { id: folderId }

      // Calculate next folder BEFORE deletion using current progressions
      const remainingProgressions = progressions.filter(
        p => parse(p.id) !== parse(progressionToDelete.id)
      )

      await deleteProgression(patient.id, progressionToDelete, () => {
        // Update context to remove the progression
        onDeleteProgression(progressionToDelete)

        // Update local state to next folder
        if (remainingProgressions.length > 0) {
          const selectedFolder = remainingProgressions[0]
          setFolderId(selectedFolder.id)
          setFolderDate(selectedFolder.startDate)
        }

        setOpenDeleteConfirmation(false)
      })
    } catch (error) {
      console.error(error)
    }
  }

  const handleEdit = async (value, editId) => {
    try {
      const progression = { id: editId ? editId : folderId, name: value }
      await renameProgression(patient.id, progression, () => {
        onRenameProgression(progression)
        setIsEditMode(false)
      })
    } catch (error) {
      console.error(error)
    }
  }

  if (progressions.length === 0)
    return <NoFolders patient={patient} onCreate={handleUpdateFolders} />

  return (
    <>
      {isMobile ? (
        <MobileFolders
          folderId={folderId}
          folderDate={folderDate}
          isCreationMode={isCreationMode}
          isEditMode={isEditMode}
          onCreate={handleCreate}
          onEdit={handleEdit}
          onSetFolderId={setFolderId}
          onSetFolderDate={setFolderDate}
          enableDelete={selectedSeries?.images?.length <= 0}
          onOpenDeleteConfirmation={() => setOpenDeleteConfirmation(true)}
          onSetIsCreationMode={() => setIsCreationMode(true)}
          onSetIsEditMode={() => setIsEditMode(true)}
        />
      ) : (
        <Folders
          isCreationMode={isCreationMode}
          isEditMode={isEditMode}
          onCreate={handleCreate}
          onEdit={handleEdit}
          enableDelete={selectedSeries?.images?.length <= 0}
          onOpenDeleteConfirmation={folder => {
            setFolderId(folder)
            setOpenDeleteConfirmation(true)
          }}
          onSetIsCreationMode={() => setIsCreationMode(true)}
          onSetIsEditMode={() => setIsEditMode(true)}
        />
      )}
      {progression && series && selectedSeries && (
        <GalleryWrapper $isMobile={isMobile ? 1 : 0}>
          {selectedSeries.name.indexOf('Standard') >= 0 ? (
            <StandardImages
              series={selectedSeries}
              folderDate={selectedProgression?.startDate}
            />
          ) : (
            <Gallery series={selectedSeries} />
          )}
        </GalleryWrapper>
      )}

      <ConfirmDelete
        isOpen={openDeleteConfirmation}
        onClose={() => setOpenDeleteConfirmation(false)}
        onConfirm={handleDelete}
      />
    </>
  )
}

export default Imaging
